import {
  ClientDealStage,
  ClientDealStepProgress,
  ClientDealStepResponse,
  ClientDealTermResponse,
} from '@interfaces/partners/responses/client-deal.response';
import { StageTimelineStep } from './components/client-stage-timeline/stage-timeline-step';

/**
 * Visão do Cliente (card 3461): textos da régua do cliente, todos no front.
 *
 * `Record<ClientDealStage, ...>` de propósito: etapa nova no backend quebra a compilação aqui, em
 * vez de virar um rótulo vazio na tela do cliente.
 */

/**
 * A ordem da régua do cliente: as **mesmas etapas que o parceiro vê** (card 3848, Breno em 31/10/2026). Comitê e
 * Cotação saíram: o backend já manda o deal nessas etapas como Análise, como para o parceiro. Os textos delas ficam nos
 * `Record` abaixo só porque o tipo `ClientDealStage` ainda as tem.
 */
export const CLIENT_DEAL_STAGES: readonly ClientDealStage[] = [
  'Proposal',
  'Consent',
  'Documentation',
  'Analysis',
  'Contracting',
  'Operation',
];

/** Rótulo curto de cada etapa, o nome na linha do tempo. */
export const CLIENT_DEAL_STAGE_LABELS: Record<ClientDealStage, string> = {
  Proposal: 'Proposta',
  Consent: 'Consentimento',
  Documentation: 'Documentação',
  Analysis: 'Análise',
  Committee: 'Comitê',
  Quotation: 'Cotação',
  Contracting: 'Contratação',
  Operation: 'Operação',
};

/**
 * O que acontece em cada etapa, uma frase, na linha do tempo da jornada (protótipo aprovado em
 * 19/10/2026). Na etapa atual vale o texto mais completo de `CLIENT_DEAL_NEXT_STEP_TEXTS`.
 *
 * ⚠️ O cliente só acompanha: nenhum texto pede que ele envie documento (decisão do Breno, 19/10/2026).
 */
export const CLIENT_DEAL_STAGE_DESCRIPTIONS: Record<ClientDealStage, string> = {
  Proposal: 'A Legatus recebe as informações da sua empresa e monta a proposta da operação.',
  Consent: 'A empresa assina o NDA de confidencialidade e a carta de autorização SCR.',
  Documentation: 'A Legatus reúne e confere os documentos da sua empresa para a análise.',
  Analysis:
    'A Legatus analisa os dados da sua empresa, estrutura a operação e busca as melhores condições com os bancos.',
  Committee: 'O comitê de crédito da Legatus avalia a operação.',
  Quotation: 'A Legatus leva a operação aos bancos em busca das melhores condições.',
  Contracting: 'As condições escolhidas são formalizadas com o banco.',
  Operation: 'A operação está contratada e em andamento.',
};

/**
 * Texto da etapa atual na linha do tempo ("o que está acontecendo agora"): um texto fixo por etapa
 * (decisão de 16/10/2026).
 *
 * ⚠️ Sem prometer aviso por e-mail ("você será avisado a cada avanço"): o sistema não manda esse
 * aviso ao cliente. Texto de Comitê é o do protótipo; os demais seguem o mesmo tom.
 */
export const CLIENT_DEAL_NEXT_STEP_TEXTS: Record<ClientDealStage, string> = {
  Proposal:
    'A Legatus está montando a proposta da sua operação a partir do que foi conversado. Não há nada pendente do seu lado por enquanto.',
  Consent:
    'Para seguir, a Legatus precisa do NDA de confidencialidade e da carta de autorização SCR assinados. Se o seu nome está entre os assinantes, use Assinar agora no card Assinaturas.',
  Documentation:
    'A Legatus está reunindo e conferindo os documentos da sua empresa para a análise. Se faltar alguma coisa, o time da Legatus fala com você.',
  Analysis:
    'A Legatus está analisando os dados da sua empresa, estruturando a operação e buscando as melhores condições com os bancos. As propostas recebidas são apresentadas a você antes de qualquer decisão.',
  Committee:
    'Sua operação está em análise pelo comitê de crédito da Legatus. Não há nada pendente do seu lado.',
  Quotation:
    'A Legatus está levando a sua operação aos bancos para buscar as melhores condições. As propostas recebidas são apresentadas a você antes de qualquer decisão.',
  Contracting:
    'As condições foram escolhidas e a operação está sendo formalizada com o banco. O time da Legatus conduz com você os contratos e os documentos finais.',
  Operation:
    'A operação foi contratada e está em andamento. Para qualquer dúvida sobre ela, fale com o time da Legatus.',
};

/**
 * A etapa que vem depois da atual, para o painel "Próximo passo". Pula a que a API marcou como dispensada
 * (`Skipped`): o cliente não deve ver anunciada uma etapa que não vai acontecer. `null` quando não sobra etapa.
 */
export function nextClientDealStage(
  currentStage: ClientDealStage,
  steps?: ClientDealStepResponse[] | null
): ClientDealStage | null {
  const index = CLIENT_DEAL_STAGES.indexOf(currentStage);
  if (index < 0) return null;
  const skipped = new Set(
    (steps ?? []).filter(step => step.progress === 'Skipped').map(step => step.stage)
  );
  return CLIENT_DEAL_STAGES.slice(index + 1).find(stage => !skipped.has(stage)) ?? null;
}

/**
 * Converte as etapas da API nas etapas da linha do tempo, sempre as de `CLIENT_DEAL_STAGES` e na ordem do cliente.
 *
 * Etapa que não veio na resposta é deduzida pela posição em relação à atual (antes = feita, depois
 * = futura): a jornada nunca fica com buraco, mesmo se o backend omitir uma etapa. A etapa atual
 * leva o texto de "o que está acontecendo agora"; as demais, a frase da etapa.
 */
export function toStageTimelineSteps(
  steps: ClientDealStepResponse[] | null | undefined,
  currentStage: ClientDealStage
): StageTimelineStep[] {
  const byStage = new Map<ClientDealStage, ClientDealStepProgress>(
    (steps ?? []).map(step => [step.stage, step.progress])
  );
  const currentIndex = CLIENT_DEAL_STAGES.indexOf(currentStage);

  return CLIENT_DEAL_STAGES.map((stage, index) => {
    const progress =
      byStage.get(stage) ??
      (index < currentIndex ? 'Done' : index === currentIndex ? 'Current' : 'Upcoming');
    return {
      stage,
      label: CLIENT_DEAL_STAGE_LABELS[stage],
      description:
        progress === 'Current'
          ? CLIENT_DEAL_NEXT_STEP_TEXTS[stage]
          : CLIENT_DEAL_STAGE_DESCRIPTIONS[stage],
      progress,
    };
  });
}

/**
 * O que a linha do termo no card "Assinaturas" mostra.
 *
 * - `none`: a Legatus ainda não gerou o termo.
 * - `notSent`: o termo existe, mas não foi enviado para assinatura (não há link ainda).
 * - `canSign`: enviado, e o usuário logado é assinante que ainda não assinou ("Assinar agora").
 * - `waiting`: enviado, e falta a assinatura de outra pessoa.
 * - `signed`: todos assinaram.
 * - `cancelled`: o envio foi cancelado pela Legatus.
 */
export type ClientTermSignatureState =
  | 'none'
  | 'notSent'
  | 'canSign'
  | 'waiting'
  | 'signed'
  | 'cancelled';

export function clientTermSignatureState(
  term: ClientDealTermResponse | null | undefined
): ClientTermSignatureState {
  if (!term) return 'none';

  switch (term.status) {
    case 'Assinado':
      return 'signed';
    case 'Cancelado':
      return 'cancelled';
    case 'Pendente':
      return 'notSent';
    default: {
      const currentUserPending = (term.signatories ?? []).some(
        signatory => signatory.isCurrentUser && !signatory.signedAt
      );
      return currentUserPending ? 'canSign' : 'waiting';
    }
  }
}
